import { useRef, useState } from 'react';

export interface DragItemProps {
  'data-drag-index': number;
  draggable: true;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

export interface UseDragReorderResult {
  containerRef: React.RefObject<HTMLDivElement>;
  getContainerProps: (onReorder: (from: number, to: number) => void) => {
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
  };
  getItemProps: (index: number) => DragItemProps;
  isDragging: (index: number) => boolean;
  isDragOver: (index: number) => boolean;
}

export function useDragReorder(itemCount: number): UseDragReorderResult {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const indexForClientY = (clientY: number): number => {
    const container = containerRef.current;
    if (!container) return itemCount;
    const items = Array.from(container.querySelectorAll<HTMLElement>('[data-drag-index]'));
    for (const item of items) {
      const rect = item.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) {
        return Number(item.dataset.dragIndex);
      }
    }
    return itemCount;
  };

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex === null) return;
    const target = indexForClientY(e.clientY);
    if (target !== dragOverIndex) setDragOverIndex(target);
  };

  const makeHandleContainerDrop = (onReorder: (from: number, to: number) => void) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex !== null) {
      const rawTarget = indexForClientY(e.clientY);
      const finalTarget = rawTarget > dragIndex ? rawTarget - 1 : rawTarget;
      if (finalTarget !== dragIndex) onReorder(dragIndex, finalTarget);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return {
    containerRef,
    getContainerProps: (onReorder: (from: number, to: number) => void) =>
      dragIndex !== null
        ? { onDragOver: handleContainerDragOver, onDrop: makeHandleContainerDrop(onReorder) }
        : {},
    getItemProps: (index: number) => ({
      'data-drag-index': index,
      draggable: true as const,
      onDragStart: handleDragStart(index),
      onDragEnd: handleDragEnd,
    }),
    isDragging: (index: number) => dragIndex === index,
    isDragOver: (index: number) =>
      dragOverIndex === index || (dragOverIndex === itemCount && index === itemCount - 1),
  };
}
